// MACHINERY V2 (Stroje) — SR detail: the attachment tiles (M2.6).
//
// Spec: data package §8 ("Attachments 1..N split by typeCode"). Reference impl:
// design_handoff_machinery_v3/machinery-sr.jsx `AttachmentTile` (lines 468-490);
// artboards 0700 / 0701 draw a 110px tile — thumbnail, name, mono size.
//
// TWO LISTS, SPLIT BY typeCode, exactly as v1 splits them
// (srDetail/ServiceReportDetailView.tsx:176-189): typeCode 1 report documents
// sit under PRÍLOHY in the problem card, typeCode 2 solving documents under the
// solution card. The split is what keeps "the photo of the fault" apart from
// "the photo of the repair".
//
// THE IMAGE TEST IS v1s, VERBATIM (:197-202): a file counts as an image when
// its dataURI starts `data:image/` OR its NAME ends in a known image extension —
// the two halves exist because a NEW upload carries a dataURI while a stored one
// may carry a URL. A non-image renders the M2.5 `FileTile` extension badge.
//
// DOWNLOAD IS AN ANCHOR, NOT A HANDLER. v1 synthesises `<a href={file.content}
// target="_blank">` and clicks it (shared/ReportFile.tsx:37-43); a real anchor
// does the same thing, is keyboard-reachable for free and needs no JS.
//
// No Tailwind, no shadcn, no MUI, no @emotion, no sx, no dt tokens.
import React from "react";

import { ServiceReportFile } from "../../models/ServiceReportsModel";
import { FileTile } from "../profile/tabs/docsRows";
import { formatDocSize } from "../profile/tabs/tabFormat";

const IMAGE_NAME = /\.(jpg|jpeg|png|gif|webp|svg|bmp)$/i;

export const isImageFile = (file: ServiceReportFile): boolean =>
  !!file.content && (file.content.startsWith("data:image/") || IMAGE_NAME.test(file.name || ""));

const Tile: React.FC<{ file: ServiceReportFile }> = ({ file }) => (
  <a
    href={file.content}
    download={file.name}
    target="_blank"
    rel="noreferrer"
    title={file.name}
    style={{
      width: 110,
      padding: 6,
      background: "var(--card)",
      border: "1px solid var(--border)",
      borderRadius: 8,
      display: "flex",
      flexDirection: "column",
      gap: 4,
      textDecoration: "none",
      color: "inherit",
      minWidth: 0,
    }}
  >
    <span
      style={{
        height: 70,
        borderRadius: 5,
        background: "var(--card-2)",
        display: "grid",
        placeItems: "center",
        overflow: "hidden",
      }}
    >
      {isImageFile(file) ? (
        <img
          src={file.content}
          alt={file.name}
          style={{ width: "100%", height: "100%", objectFit: "cover" }}
        />
      ) : (
        <FileTile fileName={file.name} />
      )}
    </span>
    <span
      style={{
        fontSize: 11,
        color: "var(--ink-2)",
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
      }}
    >
      {file.name}
    </span>
    {file.size > 0 && (
      <span style={{ fontFamily: "var(--font-mono)", fontSize: 9.5, color: "var(--faint)" }}>
        {formatDocSize(file.size)}
      </span>
    )}
  </a>
);

const SrDetailFiles: React.FC<{ files: ServiceReportFile[] }> = ({ files }) => {
  if (files.length === 0) return null;
  return (
    <div style={{ display: "flex", gap: 10, flexWrap: "wrap", minWidth: 0 }}>
      {files.map((file, i) => (
        <Tile key={file.name + "-" + i} file={file} />
      ))}
    </div>
  );
};

export default SrDetailFiles;
